import React, { useState } from 'react';
import { Bell, Menu, Search, X, User, Settings, LogOut, ChevronDown } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { login } from '../../reducer/loginSlice';
import { AuthApis } from '../../apis/authApis';

interface AdminNavbarProps {
  onMenuButtonClick: () => void;
}

const AdminNavbar: React.FC<AdminNavbarProps> = ({ onMenuButtonClick }) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const userLoginData = useSelector((state: any) => state.data.login.value);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const logOut = () => {
    AuthApis.logout('').then(
      () => {
        dispatch(login({
          email: "",
          name: "",
          password: "",
          token: "",
          data: []
        }));
        navigate('/sign-in');
      }
    ).catch(() => {
      dispatch(login({
        email: "",
        name: "",
        password: "",
        token: "",
        data: []
      }));
      navigate('/sign-in');
    });
  };

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white shadow-sm h-16 z-10 md:ml-64">
      <div className="h-full px-4 flex items-center justify-between">
        {/* Left side */}
        <div className="flex items-center">
          <button
            onClick={onMenuButtonClick}
            className="md:hidden flex items-center justify-center h-10 w-10 rounded-full hover:bg-gray-100"
          >
            <Menu className="h-5 w-5 text-gray-600" />
          </button>

          {/* Search - Mobile */}
          {searchOpen ? (
            <div className="absolute inset-0 flex items-center bg-white px-4 h-16">
              <div className="relative w-full">
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-full py-2 pl-10 pr-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
              </div>
              <button
                onClick={() => setSearchOpen(false)}
                className="ml-2 flex items-center justify-center h-10 w-10 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setSearchOpen(true)}
              className="md:hidden flex items-center justify-center h-10 w-10 rounded-full hover:bg-gray-100 ml-1"
            >
              <Search className="h-5 w-5 text-gray-600" />
            </button>
          )}

          {/* Search - Desktop */}
          <div className="hidden md:flex items-center ml-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                className="w-64 py-2 pl-10 pr-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
            </div>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-4">
          <button className="flex items-center justify-center h-10 w-10 rounded-full hover:bg-gray-100 relative">
            <Bell className="h-5 w-5 text-gray-600" />
            <span className="absolute top-1 right-1 h-4 w-4 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs">
              3
            </span>
          </button>

          <div className="relative">
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center space-x-2 focus:outline-none"
            >
              <div className="w-9 h-9 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                {userLoginData?.data?.user_data?.first_name?.charAt(0) || 'A'}
              </div>
              <span className="hidden md:flex items-center">
                <span className="mr-1 text-sm font-medium text-gray-700">
                  {userLoginData?.data?.user_data?.first_name || 'Admin'}
                </span>
                <ChevronDown className="h-4 w-4 text-gray-500" />
              </span>
            </button>

            {/* Profile Dropdown */}
            {profileOpen && (
              <div className="absolute right-0 mt-2 w-48 py-2 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">
                    {userLoginData?.data?.user_data?.first_name || 'Admin'} {userLoginData?.data?.user_data?.last_name || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {userLoginData?.data?.user_data?.email || 'admin@example.com'}
                  </p>
                </div>
                
                <Link 
                  to="/admin/profile" 
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setProfileOpen(false)}
                >
                  <User className="h-4 w-4 mr-2 text-gray-500" />
                  Profile
                </Link>
                
                <Link 
                  to="/admin/settings" 
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setProfileOpen(false)}
                >
                  <Settings className="h-4 w-4 mr-2 text-gray-500" />
                  Settings
                </Link>
                
                <div className="border-t border-gray-100 my-1"></div>
                
                <button
                  onClick={logOut}
                  className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full text-left"
                >
                  <LogOut className="h-4 w-4 mr-2 text-red-500" />
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default AdminNavbar;